/**
 * Admin mode for outgoing API requests.
 *
 * Every backend endpoint accepts a `use_minut_admin_session` query param. Rather
 * than wiring it at each call site, this module exposes a request interceptor that
 * appends `use_minut_admin_session=true` to every request whenever admin mode is
 * active. The interceptor is registered on `apiClient` in `api-client.ts`.
 *
 * Admin mode is the OR of two sources:
 *  - the URL carries `?use_minut_admin_session=true` (seeded at import, so it
 *    applies before any data has loaded), or
 *  - the selected session is an admin session (loaded from the backend).
 *
 * The flag lives in a module-level variable so the non-React interceptor can read
 * it synchronously. The session provider is the single resolver and keeps it
 * current via `setInterceptorUseAdminSession`; the interceptor reads it live on
 * every request, so an update applies to the next request without re-registering.
 *
 * Admin mode survives in-app SPA navigations because the root route keeps the URL
 * param via `retainSearchParams` (see `routes/__root.tsx`), so
 * `getUseAdminSessionParam` stays true across them. Full-page handoffs to backend
 * redirect URLs don't need the param re-appended: the request that produced the
 * redirect already carried admin mode via the interceptor, so the backend returns
 * the correct destination.
 */
import { InternalAxiosRequestConfig } from "axios"

export const USE_ADMIN_SESSION_PARAM = "use_minut_admin_session"

let shouldUseAdminSession = getHasAdminSessionParam()

/**
 * Appends `use_minut_admin_session=true` to the request's query params when admin
 * mode is active, preserving any params the call already set.
 */
export function useAdminSessionInterceptor(config: InternalAxiosRequestConfig) {
  if (shouldUseAdminSession) {
    config.params = {
      ...(config.params as Record<string, unknown> | undefined),
      [USE_ADMIN_SESSION_PARAM]: true,
    }
  }

  return config
}

// Written by the session provider whenever the resolved value changes
export function setInterceptorUseAdminSession(value: boolean) {
  shouldUseAdminSession = value
}

export function getHasAdminSessionParam() {
  const searchParams = new URLSearchParams(window.location.search)
  return searchParams.get(USE_ADMIN_SESSION_PARAM) === "true"
}
